import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ResponsiveTabs } from '../ui/responsive-tabs';
import { Input } from '../ui/input';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious 
} from '../ui/pagination';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Plus, 
  Search, 
  Filter,
  Download,
  Upload,
  Users,
  Settings,
  BarChart3,
  Shield,
  Award,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Eye,
  Edit,
  MoreHorizontal,
  Building2,
  Mail,
  Calendar,
  LogOut,
  Bell,
  Activity,
  Zap,
  Target,
  Globe,
  Database,
  PieChart,
  LineChart,
  Rocket,
  Construction,
  FileText,
  Layers,
  Palette,
  Copy,
  Pause,
  Play,
  Archive,
  Trash2
} from 'lucide-react';
import CredentialCreationWizard from '../credentials/CredentialCreationWizard';
import EnhancedCredentialWizard from '../credentials/EnhancedCredentialWizard';
import UserManagement from '../organization/UserManagement';
import W3CSchemaBuilder from '../credentials/W3CSchemaBuilder';
import CredentialTemplateDesigner from '../credentials/CredentialTemplateDesigner';
import SchemaActionsMenu from '../credentials/SchemaActionsMenu';
import { CredentialSchema, CredentialTemplate } from '../../types/W3CCredentialTypes';
import IssuerAnalyticsService, { 
  IssuerDashboardData, 
  IssuerCredentialSummary, 
  IssuerSchemaSummary,
  IssuerDashboardStats 
} from '../../services/issuerAnalyticsService';

// Coming Soon Component
const ComingSoon = ({ feature, description, icon: Icon }: { feature: string, description: string, icon: React.ComponentType<any> }) => (
  <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
    <div className="p-6 rounded-full bg-electric-steel/10 mb-6">
      <Icon className="h-12 w-12 text-electric-steel" />
    </div>
    <div className="p-4 rounded-full bg-amber-400/10 mb-4">
      <Construction className="h-8 w-8 text-amber-400" />
    </div>
    <h3 className="text-2xl font-semibold text-charcoal-ink mb-2">
      {feature} Coming Soon
    </h3>
    <p className="text-warm-slate text-lg mb-6 max-w-md">
      {description}
    </p>
    <div className="flex items-center space-x-2 text-electric-steel">
      <Rocket className="h-5 w-5" />
      <span className="font-medium">We're working hard to bring you this feature!</span>
    </div>
    <div className="mt-8 p-4 bg-cool-gray-100 rounded-lg border border-gray-200">
      <p className="text-sm text-warm-slate">
        <strong>Expected Launch:</strong> Q1 2025
      </p>
      <p className="text-sm text-warm-slate mt-1">
        This feature will be available in the full version of Credential Capital.
      </p>
    </div>
  </div>
);

export default function IssuerDashboard() {
  const { user, tenant, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCredentialWizard, setShowCredentialWizard] = useState(false);
  const [showEnhancedWizard, setShowEnhancedWizard] = useState(false);
  const [showSchemaBuilder, setShowSchemaBuilder] = useState(false);
  const [showTemplateDesigner, setShowTemplateDesigner] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState<CredentialSchema | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<CredentialTemplate | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  // Real data from API
  const [dashboardData, setDashboardData] = useState<IssuerDashboardData | null>(null);
  const [apiCredentials, setApiCredentials] = useState<IssuerCredentialSummary[]>([]);
  const [apiSchemas, setApiSchemas] = useState<IssuerSchemaSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [schemas, setSchemas] = useState<CredentialSchema[]>([]);
  const [templates, setTemplates] = useState<CredentialTemplate[]>([]);

  // Pagination state for credentials
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCredentials, setTotalCredentials] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Reset to overview tab when user or tenant changes (org switching)
  useEffect(() => {
    setActiveTab('overview');
    setSearchTerm('');
    setShowCredentialWizard(false);
    setShowEnhancedWizard(false);
    setShowSchemaBuilder(false);
    setShowTemplateDesigner(false);
    setActionMessage(null);
  }, [user?.id, tenant?.id]);

  // Fetch real data from API
  const fetchDashboardData = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch dashboard data
      const data = await IssuerAnalyticsService.getDashboardData(10);
      setDashboardData(data);
      setApiSchemas(data.schemas);
      
      // Set total credentials count from analytics
      setTotalCredentials(data.analytics?.stats?.total_credentials || 0);
      setTotalPages(Math.ceil((data.analytics?.stats?.total_credentials || 0) / pageSize));
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Unable to load dashboard data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch credentials with pagination
  const fetchCredentials = async (page: number = 1) => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch credentials with pagination
      const credentials = await IssuerAnalyticsService.getCredentials(page, pageSize);
      setApiCredentials(credentials);
      setCurrentPage(page);
      
    } catch (err) {
      console.error('Error fetching credentials:', err);
      setError('Unable to load credentials. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user?.id]);

  // Fetch credentials when component mounts or page changes
  useEffect(() => {
    if (user?.id) {
      fetchCredentials(currentPage);
    }
  }, [user?.id, currentPage, pageSize]);

  // Update total pages when pageSize changes
  useEffect(() => {
    setTotalPages(Math.ceil(totalCredentials / pageSize));
  }, [totalCredentials, pageSize]);

  // Auto-hide action messages
  useEffect(() => {
    if (actionMessage) {
      const timer = setTimeout(() => {
        setActionMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [actionMessage]);



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-verified-green text-white';
      case 'expired':
        return 'bg-red-500 text-white';
      case 'pending_renewal':
        return 'bg-amber-500 text-white';
      case 'expiring_soon':
        return 'bg-orange-500 text-white';
      case 'under_review':
        return 'bg-blue-500 text-white';
      default:
        return 'bg-warm-slate text-white';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-3 w-3" />;
      case 'expired':
        return <AlertTriangle className="h-3 w-3" />;
      case 'pending_renewal':
        return <Clock className="h-3 w-3" />;
      case 'expiring_soon':
        return <Clock className="h-3 w-3" />;
      case 'under_review':
        return <Eye className="h-3 w-3" />;
      default:
        return <AlertTriangle className="h-3 w-3" />;
    }
  };

  // Calculate stats from real API data only - no fallback in production
  const stats = useMemo(() => {
    if (!dashboardData?.analytics?.stats) {
      return {
        totalCredentials: 0,
        activeCredentials: 0,
        totalVerifications: 0,
        thisMonth: 0,
        totalSchemas: 0,
        activeSchemas: 0,
        totalTemplates: 0,
        monthlyGrowth: 0
      };
    }
    
    const apiStats = dashboardData.analytics.stats;
    return {
      totalCredentials: apiStats.total_credentials,
      activeCredentials: apiStats.active_credentials,
      totalVerifications: apiStats.total_verifications,
      thisMonth: apiStats.this_month_credentials,
      totalSchemas: apiStats.active_schemas,
      activeSchemas: apiStats.active_schemas,
      totalTemplates: apiStats.total_templates,
      monthlyGrowth: apiStats.monthly_growth
    };
  }, [dashboardData]);

  const handleSaveSchema = (schema: CredentialSchema) => {
    const existingIndex = schemas.findIndex(s => s.id === schema.id);
    if (existingIndex !== -1) {
      setSchemas(prev => prev.map((s, i) => i === existingIndex ? schema : s));
    } else {
      setSchemas(prev => [...prev, schema]);
    }
    setShowSchemaBuilder(false);
    setSelectedSchema(null);
  };

  const handleSaveTemplate = (template: CredentialTemplate) => {
    const existingIndex = templates.findIndex(t => t.id === template.id);
    if (existingIndex !== -1) {
      setTemplates(prev => prev.map((t, i) => i === existingIndex ? template : t));
    } else {
      setTemplates(prev => [...prev, template]);
    }
    setShowTemplateDesigner(false);
    setSelectedTemplate(null);
  };

  const handleEditSchema = (schema: CredentialSchema) => {
    setSelectedSchema(schema);
    setShowSchemaBuilder(true);
  };

  const handleEditTemplate = (template: CredentialTemplate) => {
    setSelectedTemplate(template);
    setShowTemplateDesigner(true);
  };

  // Schema action handlers
  const handleDuplicateSchema = (schema: CredentialSchema) => {
    const duplicatedSchema: CredentialSchema = {
      ...schema,
      id: `schema_${Date.now()}`,
      name: `${schema.name} (Copy)`,
      version: '1.0',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: user?.id || 'current-user'
    };
    setSchemas(prev => [...prev, duplicatedSchema]);
    setActionMessage({ type: 'success', message: `Schema "${schema.name}" has been duplicated successfully.` });
  };

  const handleSuspendSchema = (schemaId: string) => {
    setSchemas(prev => prev.map(s => 
      s.id === schemaId ? { ...s, isActive: false, updatedAt: new Date().toISOString() } : s
    ));
    const schema = schemas.find(s => s.id === schemaId);
    setActionMessage({ type: 'success', message: `Schema "${schema?.name}" has been suspended.` });
  };

  const handleResumeSchema = (schemaId: string) => {
    setSchemas(prev => prev.map(s => 
      s.id === schemaId ? { ...s, isActive: true, updatedAt: new Date().toISOString() } : s
    ));
    const schema = schemas.find(s => s.id === schemaId);
    setActionMessage({ type: 'success', message: `Schema "${schema?.name}" has been resumed.` });
  };

  const handleArchiveSchema = (schemaId: string) => {
    setSchemas(prev => prev.map(s => 
      s.id === schemaId ? { ...s, isActive: false, updatedAt: new Date().toISOString() } : s
    ));
    const schema = schemas.find(s => s.id === schemaId);
    setActionMessage({ type: 'success', message: `Schema "${schema?.name}" has been archived.` });
  };

  const handleDeleteSchema = (schemaId: string) => {
    const schema = schemas.find(s => s.id === schemaId);
    setSchemas(prev => prev.filter(s => s.id !== schemaId));
    setActionMessage({ type: 'success', message: `Schema "${schema?.name}" has been deleted.` });
  };

  const handleExportSchema = (schema: CredentialSchema) => {
    const exportData = {
      id: schema.id,
      name: schema.name,
      version: schema.version,
      description: schema.description,
      fields: schema.fields,
      createdAt: schema.createdAt,
      updatedAt: schema.updatedAt
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `${schema.name.replace(/\s+/g, '_')}_schema_v${schema.version}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    setActionMessage({ type: 'success', message: `Schema "${schema.name}" has been exported.` });
  };

  const handleViewUsage = (schema: CredentialSchema) => {
    // This would open the usage dialog - already handled in SchemaActionsMenu
    console.log('View usage for schema:', schema.name);
  };

  // Generate analytics data based on tenant
  const generateAnalyticsData = () => {
    const tenantName = tenant?.name || 'Organization';
    const baseData = {
      overview: {
        totalCredentials: stats.totalCredentials,
        activeCredentials: stats.activeCredentials,
        totalVerifications: stats.totalVerifications,
        successRate: 94.2,
        avgResponseTime: 1.8,
        monthlyGrowth: 12.5
      },
      credentialTrends: [
        { month: 'Jan', issued: 45, verified: 89 },
        { month: 'Feb', issued: 52, verified: 94 },
        { month: 'Mar', issued: 61, verified: 112 },
        { month: 'Apr', issued: 48, verified: 98 },
        { month: 'May', issued: 67, verified: 125 },
        { month: 'Jun', issued: 73, verified: 141 }
      ],
      verificationsByType: [
        { type: 'Policy Evaluations', count: 234, percentage: 45 },
        { type: 'Agent Verifications', count: 156, percentage: 30 },
        { type: 'Direct Verifications', count: 98, percentage: 19 },
        { type: 'Bulk Verifications', count: 32, percentage: 6 }
      ],
      topVerifiers: [
        { name: 'Healthcare Partners', verifications: 89, category: 'Healthcare' },
        { name: 'Metro University', verifications: 67, category: 'Education' },
        { name: 'City Government', verifications: 45, category: 'Government' },
        { name: 'TechCorp Inc.', verifications: 34, category: 'Technology' }
      ]
    };

    return baseData;
  };

  const analyticsData = generateAnalyticsData();

  if (showCredentialWizard) {
    return (
      <CredentialCreationWizard 
        onComplete={() => setShowCredentialWizard(false)}
        onCancel={() => setShowCredentialWizard(false)}
      />
    );
  }

  if (showEnhancedWizard) {
    return (
      <EnhancedCredentialWizard
        onComplete={() => setShowEnhancedWizard(false)}
        onCancel={() => setShowEnhancedWizard(false)}
      />
    );
  }

  if (showSchemaBuilder) {
    return (
      <W3CSchemaBuilder
        schema={selectedSchema || undefined}
        onSave={handleSaveSchema}
        onCancel={() => {
          setShowSchemaBuilder(false);
          setSelectedSchema(null);
        }}
        isEditing={!!selectedSchema}
      />
    );
  }

  if (showTemplateDesigner) {
    if (!selectedSchema) {
      // Show schema selection first
      return (
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-display font-bold text-charcoal-ink mb-2">
              Select Schema for Template
            </h1>
            <p className="text-warm-slate">
              Choose the credential schema to create a template for
            </p>
          </div>
          
          <div className="grid gap-4">
            {schemas.map(schema => (
              <Card 
                key={schema.id}
                className="cursor-pointer transition-all hover:shadow-lg border-gray-200 hover:border-electric-steel"
                onClick={() => {
                  setSelectedSchema(schema);
                }}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold mb-2">{schema.name}</h3>
                      <p className="text-sm text-warm-slate mb-3">{schema.description}</p>
                      <div className="flex items-center space-x-4 text-sm text-warm-slate">
                        <span>Version: {schema.version}</span>
                        <span>Fields: {schema.fields.length}</span>
                        <span>Required: {schema.fields.filter(f => f.required).length}</span>
                      </div>
                    </div>
                    <Badge variant="outline">W3C Compliant</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center">
            <Button variant="outline" onClick={() => setShowTemplateDesigner(false)}>
              Cancel
            </Button>
          </div>
        </div>
      );
    }

    return (
      <CredentialTemplateDesigner
        template={selectedTemplate || undefined}
        schema={selectedSchema}
        onSave={handleSaveTemplate}
        onCancel={() => {
          setShowTemplateDesigner(false);
          setSelectedTemplate(null);
          setSelectedSchema(null);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-cool-gray-100">
      {/* Header */}
      <header className="credential-capital-header shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white/10 p-2 rounded-lg">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-display font-bold text-white">
                  Admin Dashboard
                </h1>
                <div className="flex items-center space-x-2">
                  <p className="text-blue-100">
                    Welcome back, {user?.name}
                  </p>
                  {tenant && (
                    <Badge variant="outline" className="text-xs border-white/20 text-blue-100">
                      <Building2 className="h-3 w-3 mr-1" />
                      {tenant.name}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" className="credential-capital-button-header">
                <Bell className="h-4 w-4 mr-2" />
                Notifications
              </Button>
              <Button 
                onClick={() => setShowEnhancedWizard(true)}
                size="sm"
                className="bg-white text-midnight-indigo hover:bg-gray-50 font-medium"
              >
                <Plus className="h-4 w-4 mr-2" />
                Issue Credentials
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={logout}
                className="credential-capital-button-header"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full max-w-7xl mx-auto p-6 space-y-6">
        {/* Action Message */}
        {actionMessage && (
          <Alert className={`${actionMessage.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            {actionMessage.type === 'success' ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-red-600" />
            )}
            <AlertDescription className={actionMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}>
              {actionMessage.message}
            </AlertDescription>
          </Alert>
        )}



        {/* Error Alert */}
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
            <div className="mt-3">
              <Button 
                onClick={fetchDashboardData} 
                variant="outline" 
                size="sm"
                className="text-red-700 border-red-300 hover:bg-red-100"
              >
                <span className="mr-2">↻</span>
                Retry
              </Button>
            </div>
          </Alert>
        )}

        {/* Enhanced Stats Cards */}
        {loading && (
          <div className="text-center py-4 text-warm-slate">
            Loading dashboard data...
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <Card className="credential-capital-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warm-slate">Total Credentials</p>
                  <p className="text-2xl font-semibold text-charcoal-ink">
                    {loading ? '...' : stats.totalCredentials || 0}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-electric-steel/10">
                  <Award className="h-6 w-6 text-electric-steel" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="credential-capital-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warm-slate">Active Credentials</p>
                  <p className="text-2xl font-semibold text-verified-green">
                    {loading ? '...' : stats.activeCredentials || 0}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-verified-green/10">
                  <CheckCircle className="h-6 w-6 text-verified-green" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="credential-capital-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warm-slate">Total Verifications</p>
                  <p className="text-2xl font-semibold text-charcoal-ink">
                    {loading ? '...' : stats.totalVerifications || 0}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-electric-steel/10">
                  <Shield className="h-6 w-6 text-electric-steel" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="credential-capital-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warm-slate">Active Schemas</p>
                  <p className="text-2xl font-semibold text-charcoal-ink">
                    {loading ? '...' : stats.activeSchemas || 0}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-electric-steel/10">
                  <Layers className="h-6 w-6 text-electric-steel" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="credential-capital-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warm-slate">This Month</p>
                  <p className="text-2xl font-semibold text-charcoal-ink">
                    {loading ? '...' : stats.thisMonth || 0}
                  </p>
                  <div className="flex items-center mt-1">
                    <TrendingUp className="h-3 w-3 text-verified-green mr-1" />
                    <span className="text-xs text-verified-green">
                      {stats.monthlyGrowth > 0 ? '+' : ''}{(stats.monthlyGrowth || 0).toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="p-3 rounded-full bg-verified-green/10">
                  <Calendar className="h-6 w-6 text-verified-green" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>



        {/* Main Content Tabs */}
        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <ResponsiveTabs value={activeTab} onValueChange={setActiveTab}>
              <TabsTrigger value="overview" className="credential-capital-tab-trigger">Overview</TabsTrigger>
              <TabsTrigger value="credentials" className="credential-capital-tab-trigger">Credentials</TabsTrigger>
              <TabsTrigger value="schemas" className="credential-capital-tab-trigger">Schemas</TabsTrigger>
              <TabsTrigger value="templates" className="credential-capital-tab-trigger">Templates</TabsTrigger>
              <TabsTrigger value="policies" className="credential-capital-tab-trigger">
                <div className="flex items-center space-x-1">
                  <span>Policies</span>
                  <Badge className="bg-amber-400 text-amber-900 text-xs px-1.5 py-0.5">Soon</Badge>
                </div>
              </TabsTrigger>
              <TabsTrigger value="agents" className="credential-capital-tab-trigger">
                <div className="flex items-center space-x-1">
                  <span>Agents</span>
                  <Badge className="bg-amber-400 text-amber-900 text-xs px-1.5 py-0.5">Soon</Badge>
                </div>
              </TabsTrigger>
              <TabsTrigger value="analytics" className="credential-capital-tab-trigger">Analytics</TabsTrigger>
              <TabsTrigger value="users" className="credential-capital-tab-trigger">Users</TabsTrigger>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {/* Recent Activity */}
                  <Card className="border">
                    <CardHeader>
                      <CardTitle className="text-lg">Recent Activity</CardTitle>
                      <CardDescription>Latest credential issuances and verifications</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {loading ? (
                        <div className="text-center py-4 text-warm-slate">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-electric-steel mx-auto mb-2"></div>
                          <p className="text-xs">Loading...</p>
                        </div>
                      ) : apiCredentials.length === 0 ? (
                        <div className="text-center py-4 text-warm-slate">
                          <p className="text-xs">No recent activity</p>
                        </div>
                      ) : (
                        apiCredentials.slice(0, 3).map((credential) => (
                          <div key={credential.id} className="flex items-center space-x-3">
                            <div className="p-2 rounded-full bg-verified-green/10">
                              <Award className="h-4 w-4 text-verified-green" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-charcoal-ink">
                                {credential.title} issued to {credential.recipient}
                              </p>
                              <p className="text-xs text-warm-slate">
                                {new Date(credential.issued_date).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge className={getStatusColor(credential.status)}>
                              <div className="flex items-center space-x-1">
                                {getStatusIcon(credential.status)}
                                <span>{credential.status.replace('_', ' ')}</span>
                              </div>
                            </Badge>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>

                  {/* Quick Actions */}
                  <Card className="border">
                    <CardHeader>
                      <CardTitle className="text-lg">Quick Actions</CardTitle>
                      <CardDescription>Available features and upcoming enhancements</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Button 
                        onClick={() => setShowEnhancedWizard(true)}
                        className="w-full justify-start credential-capital-button-primary mobile:mobile-button-text-long mobile:mobile-button-stack"
                      >
                        <Plus className="h-4 w-4 mobile:mr-0" />
                        <span className="mobile:hidden">Issue New Credentials (Complete Flow)</span>
                        <span className="hidden mobile:block">Issue New Credentials</span>
                      </Button>
                      <Button 
                        onClick={() => setShowCredentialWizard(true)}
                        variant="outline"
                        className="w-full justify-start mobile:mobile-button-text mobile:mobile-button-stack"
                      >
                        <Plus className="h-4 w-4 mobile:mr-0" />
                        <span className="mobile:hidden">Quick Issue (Simple)</span>
                        <span className="hidden mobile:block">Quick Issue</span>
                      </Button>
                      <Button 
                        onClick={() => setShowSchemaBuilder(true)}
                        className="w-full justify-start credential-capital-button-primary mobile:mobile-button-text mobile:mobile-button-stack"
                      >
                        <Layers className="h-4 w-4 mobile:mr-0" />
                        Create Schema
                      </Button>
                      <Button 
                        onClick={() => setShowTemplateDesigner(true)}
                        className="w-full justify-start credential-capital-button-primary mobile:mobile-button-text mobile:mobile-button-stack"
                      >
                        <Palette className="h-4 w-4 mobile:mr-0" />
                        Design Template
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start mobile:mobile-button-text mobile:mobile-button-stack"
                        onClick={() => setActiveTab('analytics')}
                      >
                        <BarChart3 className="h-4 w-4 mobile:mr-0" />
                        View Analytics
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full justify-start mobile:mobile-button-text mobile:mobile-button-stack"
                        onClick={() => setActiveTab('users')}
                      >
                        <Users className="h-4 w-4 mobile:mr-0" />
                        Manage Users
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Credentials Tab */}
              <TabsContent value="credentials" className="space-y-6 mt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-ink">Issued Credentials</h3>
                    <p className="text-sm text-warm-slate">
                      {loading ? 'Loading...' : `${totalCredentials} total credentials`}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-warm-slate" />
                      <Input
                        placeholder="Search credentials..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 w-64"
                      />
                    </div>
                    <Button variant="outline" size="sm">
                      <Filter className="h-4 w-4 mr-2" />
                      Filter
                    </Button>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                    </select>
                  </div>
                </div>

                <div className="grid gap-4">
                  {loading ? (
                    <div className="text-center py-8 text-warm-slate">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-steel mx-auto mb-2"></div>
                      <p>Loading credentials...</p>
                    </div>
                  ) : apiCredentials.length === 0 ? (
                    <div className="text-center py-8 text-warm-slate">
                      <Award className="h-8 w-8 mx-auto mb-2 text-warm-slate/50" />
                      <p>No credentials found</p>
                    </div>
                  ) : (
                    apiCredentials
                      .filter(credential => 
                        credential.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        credential.recipient?.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((credential) => (
                        <Card key={credential.id} className="border">
                          <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="p-3 rounded-full bg-electric-steel/10">
                                  <Award className="h-6 w-6 text-electric-steel" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-charcoal-ink">{credential.title}</h4>
                                  <div className="flex items-center space-x-4 mt-1">
                                    <span className="text-sm text-warm-slate">
                                      Issued to: {credential.recipient}
                                    </span>
                                    <span className="text-sm text-warm-slate">
                                      <Mail className="h-3 w-3 inline mr-1" />
                                      {credential.recipient_email}
                                    </span>
                                    <span className="text-sm text-warm-slate">
                                      <Calendar className="h-3 w-3 inline mr-1" />
                                      {new Date(credential.issued_date).toLocaleDateString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-4">
                                <div className="text-right">
                                  <p className="text-sm text-warm-slate">Verifications</p>
                                  <p className="font-semibold text-charcoal-ink">{credential.verification_count}</p>
                                </div>
                                <Badge className={getStatusColor(credential.status)}>
                                  <div className="flex items-center space-x-1">
                                    {getStatusIcon(credential.status)}
                                    <span>{credential.status.replace('_', ' ')}</span>
                                  </div>
                                </Badge>
                                <Button variant="outline" size="sm">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                  )}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="text-sm text-warm-slate">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCredentials)} of {totalCredentials} credentials
                    </div>
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious 
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                        
                        {/* Page Numbers */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <PaginationItem key={pageNum}>
                              <PaginationLink
                                onClick={() => setCurrentPage(pageNum)}
                                isActive={currentPage === pageNum}
                                className="cursor-pointer"
                              >
                                {pageNum}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        })}
                        
                        <PaginationItem>
                          <PaginationNext 
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </TabsContent>

              {/* Schemas Tab */}
              <TabsContent value="schemas" className="space-y-6 mt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-ink">W3C Credential Schemas</h3>
                    <p className="text-sm text-warm-slate">
                      {loading ? 'Loading...' : `${apiSchemas.length} schemas available`}
                    </p>
                  </div>
                  <Button 
                    onClick={() => setShowSchemaBuilder(true)}
                    className="credential-capital-button-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Schema
                  </Button>
                </div>

                <div className="grid gap-4">
                  {loading ? (
                    <div className="text-center py-8 text-warm-slate">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-steel mx-auto mb-2"></div>
                      <p>Loading schemas...</p>
                    </div>
                  ) : apiSchemas.length === 0 ? (
                    <div className="text-center py-12">
                      <Layers className="h-16 w-16 text-warm-slate mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-semibold text-charcoal-ink mb-2">No schemas found</h3>
                      <p className="text-warm-slate mb-4">No schemas available for your organization</p>
                    </div>
                  ) : (
                    apiSchemas.map((schema) => (
                      <Card key={schema.id} className="border">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="p-3 rounded-full bg-electric-steel/10">
                                <Layers className="h-6 w-6 text-electric-steel" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-charcoal-ink">{schema.name}</h4>
                                <p className="text-sm text-warm-slate mb-2">{schema.description}</p>
                                <div className="flex items-center space-x-4 text-sm text-warm-slate">
                                  <span>Version: {schema.version}</span>
                                  <span>Fields: {schema.field_count}</span>
                                  <span>Required: {schema.required_field_count}</span>
                                  <span>Created: {new Date(schema.created_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4">
                              <Badge className={schema.is_active ? 'bg-verified-green text-white' : 'bg-warm-slate text-white'}>
                                {schema.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              <Badge variant="outline">W3C Compliant</Badge>
                              <div className="text-right">
                                <p className="text-sm text-warm-slate">Credentials</p>
                                <p className="font-semibold text-charcoal-ink">{schema.credential_count}</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Templates Tab */}
              <TabsContent value="templates" className="space-y-6 mt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-ink">Credential Templates</h3>
                    <p className="text-sm text-warm-slate">Design visual layouts for your credentials</p>
                  </div>
                  <Button 
                    onClick={() => setShowTemplateDesigner(true)}
                    className="credential-capital-button-primary"
                    disabled={apiSchemas.length === 0}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Template
                  </Button>
                </div>

                {apiSchemas.length === 0 && (
                  <div className="text-center py-12">
                    <Palette className="h-16 w-16 text-warm-slate mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-semibold text-charcoal-ink mb-2">Create a schema first</h3>
                    <p className="text-warm-slate mb-4">You need to create a credential schema before designing templates</p>
                    <Button 
                      onClick={() => setShowSchemaBuilder(true)}
                      className="credential-capital-button-primary"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Schema
                    </Button>
                  </div>
                )}

                {apiSchemas.length > 0 && (
                  <div className="grid gap-4">
                    {apiSchemas.map((schema) => (
                      <Card key={schema.id} className="border">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="p-3 rounded-full bg-electric-steel/10">
                                <Palette className="h-6 w-6 text-electric-steel" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-charcoal-ink">{schema.name} Template</h4>
                                <p className="text-sm text-warm-slate">Template for {schema.name} credentials</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Badge variant="outline">Linked to Schema</Badge>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  // Find the full schema object from apiSchemas
                                  const fullSchema = apiSchemas.find(s => s.id === schema.id);
                                  if (fullSchema) {
                                    // Convert IssuerSchemaSummary to CredentialSchema format
                                    const credentialSchema: CredentialSchema = {
                                      id: fullSchema.id,
                                      name: fullSchema.name,
                                      version: fullSchema.version,
                                      description: fullSchema.description,
                                      fields: [], // Fields will be fetched when needed
                                      isActive: fullSchema.is_active,
                                      createdAt: fullSchema.created_at,
                                      updatedAt: fullSchema.created_at,
                                      createdBy: user?.id || 'unknown',
                                      organizationId: tenant?.id || 'unknown'
                                    };
                                    setSelectedSchema(credentialSchema);
                                    setShowTemplateDesigner(true);
                                  }
                                }}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View/Edit Template
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Policies Tab - Coming Soon */}
              <TabsContent value="policies" className="mt-6">
                <ComingSoon 
                  feature="Policy Management" 
                  description="Create and manage sophisticated verification policies with our visual policy builder. Automate credential verification decisions based on your organization's specific requirements."
                  icon={Shield}
                />
              </TabsContent>

              {/* Agents Tab - Coming Soon */}
              <TabsContent value="agents" className="mt-6">
                <ComingSoon 
                  feature="Verification Agents" 
                  description="Deploy automated verification agents that can handle credential verification workflows 24/7. Set up scheduled verifications and integrate with external systems."
                  icon={Settings}
                />
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics" className="space-y-6 mt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-ink">Analytics Dashboard</h3>
                    <p className="text-sm text-warm-slate">Insights and metrics for {tenant?.name || 'your organization'}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-red-600 font-semibold text-sm bg-red-50 px-3 py-1 rounded-full border border-red-200">Coming Soon</span>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export Report
                    </Button>
                    <Button variant="outline" size="sm">
                      <Calendar className="h-4 w-4 mr-2" />
                      Date Range
                    </Button>
                  </div>
                </div>

                {/* Analytics Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card className="credential-capital-card">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-warm-slate">Success Rate</p>
                          <p className="text-2xl font-semibold text-verified-green">
                            {analyticsData.overview.successRate}%
                          </p>
                        </div>
                        <div className="p-3 rounded-full bg-verified-green/10">
                          <Target className="h-6 w-6 text-verified-green" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="credential-capital-card">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-warm-slate">Avg Response Time</p>
                          <p className="text-2xl font-semibold text-electric-steel">
                            {analyticsData.overview.avgResponseTime}s
                          </p>
                        </div>
                        <div className="p-3 rounded-full bg-electric-steel/10">
                          <Zap className="h-6 w-6 text-electric-steel" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="credential-capital-card">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-warm-slate">Monthly Growth</p>
                          <p className="text-2xl font-semibold text-charcoal-ink">
                            +{analyticsData.overview.monthlyGrowth}%
                          </p>
                        </div>
                        <div className="p-3 rounded-full bg-verified-green/10">
                          <TrendingUp className="h-6 w-6 text-verified-green" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="credential-capital-card">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-warm-slate">Active Schemas</p>
                          <p className="text-2xl font-semibold text-charcoal-ink">
                            {stats.activeSchemas}
                          </p>
                        </div>
                        <div className="p-3 rounded-full bg-electric-steel/10">
                          <Layers className="h-6 w-6 text-electric-steel" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users" className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-ink">User Management</h3>
                    <p className="text-sm text-warm-slate">Manage users and their permissions</p>
                  </div>
                  <span className="text-red-600 font-semibold text-sm bg-red-50 px-3 py-1 rounded-full border border-red-200">Coming Soon</span>
                </div>
                <UserManagement />
              </TabsContent>
            </ResponsiveTabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
  }